import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FolderKanban, Plus, X, Trash2, Pencil, RefreshCw, Users as UsersIcon, UserPlus, SlidersHorizontal, Gauge, MoreVertical, BarChart3, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  getProjects, createProject, updateProject, deleteProject,
  getProjectMembers, addProjectMember, updateProjectMember, removeProjectMember,
  getTiers, getUsers, resetProjectRateLimits, resetMemberRateLimits, getProjectUsage,
} from '../api';

const WIN_LABEL = { min: 'Per minute', hour: 'Per hour', '6h': 'Per 6 hours', day: 'Per day' };

const EMPTY = { name: '', description: '', tier_id: '', instruction: '' };
const RL_FIELDS = [
  { key: 'per_minute', label: 'min' },
  { key: 'per_hour', label: 'hour' },
  { key: 'per_6h', label: '6h' },
  { key: 'per_day', label: 'day' },
];
const ZERO = { per_minute: 0, per_hour: 0, per_6h: 0, per_day: 0 };

// Per-member, per-model RL override editor. Overrides win over the tier's
// per-model limits for this member; blank rows (all 0) are dropped on save.
function MemberLimitsModal({ project, member, tierModels, onClose, onSaved }) {
  const [ov, setOv] = useState(() => {
    const base = {};
    Object.entries(member.rl_overrides || {}).forEach(([m, r]) => { base[m] = { ...ZERO, ...r }; });
    return base;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const rows = ['_default', ...tierModels];
  const getRow = (m) => ov[m] || { ...ZERO };
  const setCell = (m, k, v) => setOv((s) => ({ ...s, [m]: { ...getRow(m), [k]: parseInt(v || 0, 10) || 0 } }));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      // keep only rows with at least one positive window
      const cleaned = {};
      rows.forEach((m) => {
        const r = getRow(m);
        if (RL_FIELDS.some((f) => (r[f.key] || 0) > 0)) cleaned[m] = r;
      });
      await updateProjectMember(project.project_id, member.user_id, { rl_overrides: cleaned });
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-lg font-semibold text-fg">RL overrides — {member.email || member.user_id}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-fg-muted text-sm mb-4">Per-model limits for this member only (0 = inherit the tier). Requests per window.</p>

        <div className="border border-field-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-fg-muted bg-field/40">
                <th className="text-left px-3 py-2 font-medium">Model</th>
                {RL_FIELDS.map((r) => <th key={r.key} className="px-2 py-2 font-medium">{r.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m} className="border-t border-field-border">
                  <td className="px-3 py-1.5 font-mono text-xs text-fg">
                    {m === '_default' ? <span className="text-fg-muted italic">default</span> : m}
                  </td>
                  {RL_FIELDS.map((r) => (
                    <td key={r.key} className="px-1 py-1">
                      <input type="number" min="0" value={getRow(m)[r.key]} onChange={(e) => setCell(m, r.key, e.target.value)}
                        className="w-16 px-2 py-1 bg-field border border-field-border rounded text-fg text-xs text-center" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && <p className="text-xs text-red-400 mt-3">{error}</p>}

        <div className="flex gap-3 pt-5">
          <button onClick={save} disabled={busy} className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm disabled:opacity-50">{busy ? 'Saving…' : 'Save overrides'}</button>
          <button onClick={onClose} className="px-6 py-2.5 bg-panel border border-panel-border text-fg rounded-lg hover:border-orange text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Create / edit a project ──
function ProjectModal({ project, tiers, onClose, onSaved }) {
  const [f, setF] = useState(project
    ? { name: project.name, description: project.description || '', tier_id: project.tier_id || '', instruction: project.instruction || '' }
    : EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!f.name.trim()) { setError('Name is required'); return; }
    setBusy(true); setError(null);
    try {
      const payload = { ...f, tier_id: f.tier_id || null };
      if (project) await updateProject(project.project_id, payload); else await createProject(payload);
      onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-xl font-semibold text-fg">{project ? 'Edit team' : 'New team'}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-fg-muted mb-1">Name *</label>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="e.g. Trading-Bot, Risk-Reports" />
          </div>
          <div>
            <label className="block text-sm text-fg-muted mb-1">Description</label>
            <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="Optional" />
          </div>
          <div>
            <label className="block text-sm text-fg-muted mb-1">Tier (allowed models + rate limits)</label>
            <select value={f.tier_id} onChange={(e) => setF({ ...f, tier_id: e.target.value })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm">
              <option value="">Default tier</option>
              {tiers.map((t) => <option key={t.tier_id} value={t.tier_id}>{t.name}{t.is_default ? ' (default)' : ''}</option>)}
            </select>
            <p className="text-xs text-fg-muted mt-1">Every member of this team gets this tier's models &amp; limits.</p>
          </div>
          <div>
            <label className="block text-sm text-fg-muted mb-1">Team instruction (system prompt)</label>
            <textarea value={f.instruction} onChange={(e) => setF({ ...f, instruction: e.target.value })} rows={4}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm"
              placeholder="e.g. You are an assistant for the Trading-Bot team. Never reveal internal client names." />
            <p className="text-xs text-fg-muted mt-1">Prepended to the system prompt of <b>every</b> request made from this team.</p>
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        <div className="flex gap-3 pt-5">
          <button onClick={submit} disabled={busy} className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm disabled:opacity-50">
            {busy ? 'Saving…' : (project ? 'Save changes' : 'Create team')}
          </button>
          <button onClick={onClose} className="px-6 py-2.5 bg-panel border border-panel-border text-fg rounded-lg hover:border-orange text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Manage members of a project ──
const USER_PICKER_DEFAULT_LIMIT = 5;
const USER_PICKER_SEARCH_LIMIT = 20;
const ROSTER_PAGE_SIZE = 5;

function MembersModal({ project, tiers, onClose }) {
  const [members, setMembers] = useState([]);
  const [memberTotal, setMemberTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addUser, setAddUser] = useState('');
  const [addTier, setAddTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);          // green success line
  const [limitsFor, setLimitsFor] = useState(null);   // member whose RL overrides are open

  // Member roster: server-paginated and searchable rather than rendering the
  // full team in one pass.
  const [rosterQueryInput, setRosterQueryInput] = useState('');
  const [rosterQuery, setRosterQuery] = useState('');
  const [rosterPage, setRosterPage] = useState(1);

  // "Add member" picker: server-side search over org users, capped and paged
  // like the roster above.
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const [userSearching, setUserSearching] = useState(false);
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const flashNotice = (text) => { setNotice(text); setTimeout(() => setNotice(null), 3500); };

  const resetTeamRL = async () => {
    if (!window.confirm(`Reset rate limits for the whole team "${project.name}"? Clears every member's consumed quota. Limits are unchanged.`)) return;
    try { const r = await resetProjectRateLimits(project.project_id); flashNotice(`Team rate limits reset — ${r.data.cleared} counter(s) cleared.`); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to reset team rate limits'); }
  };

  const resetMemberRL = async (m) => {
    if (!window.confirm(`Reset rate limits for ${m.email || m.user_id} in this team? Clears their consumed quota here only.`)) return;
    try { const r = await resetMemberRateLimits(project.project_id, m.user_id); flashNotice(`Reset — ${r.data.cleared} counter(s) cleared.`); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to reset member rate limits'); }
  };

  const tierModelsFor = (m) => {
    const tid = m.tier_id || project.tier_id;
    const t = tiers.find((x) => x.tier_id === tid);
    return t?.models || [];
  };

  // Debounce roster search input; reset to page 1 on every new query.
  useEffect(() => {
    const t = setTimeout(() => { setRosterQuery(rosterQueryInput.trim()); setRosterPage(1); }, rosterQueryInput ? 250 : 0);
    return () => clearTimeout(t);
  }, [rosterQueryInput]);

  // `loading` gates only the initial render. Search/page refetches swap the
  // list contents in place without it, so typing doesn't flash the
  // "Loading…" placeholder on every keystroke.
  //
  // rosterFetchSeq: only the latest fetchMembers call may write state, so a
  // slower, older response can't overwrite a newer one.
  const rosterFetchSeq = useRef(0);
  const fetchMembers = useCallback(async () => {
    const seq = ++rosterFetchSeq.current;
    try {
      const r = await getProjectMembers(project.project_id, rosterPage, ROSTER_PAGE_SIZE, rosterQuery);
      if (seq !== rosterFetchSeq.current) return;
      setMembers(r.data.members || []);
      setMemberTotal(r.data.total || 0);
    } catch {
      if (seq === rosterFetchSeq.current) setError('Failed to load members');
    } finally {
      if (seq === rosterFetchSeq.current) setLoading(false);
    }
  }, [project.project_id, rosterPage, rosterQuery]);
  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  // Flags existing members in the "add member" picker below instead of hiding
  // them, so a search that only matches a current member says so rather than
  // reading as "no matching users". Scoped to the loaded roster page/search
  // only, not the full team; addProjectMember still 409s on a stale duplicate.
  const memberIds = new Set(members.map((m) => m.user_id));

  useEffect(() => {
    let cancelled = false;
    const query = userQuery.trim();
    setUserSearching(true);
    const t = setTimeout(async () => {
      try {
        const limit = query ? USER_PICKER_SEARCH_LIMIT : USER_PICKER_DEFAULT_LIMIT;
        const r = await getUsers(1, limit, query);
        if (cancelled) return;
        setUserResults(r.data.users || []);
      } catch { if (!cancelled) setUserResults([]); }
      finally { if (!cancelled) setUserSearching(false); }
    }, query ? 250 : 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [userQuery, project.project_id]);

  const selectUser = (u) => {
    setAddUser(u.user_id);
    setUserQuery(u.user_email_id);
    setUserPickerOpen(false);
  };

  const add = async () => {
    if (!addUser) { setError('Pick a user'); return; }
    // Fast path for the common case; the 409 below is the authoritative check
    // since memberIds only covers the loaded roster page.
    if (memberIds.has(addUser)) { setError('User already exists'); return; }
    setBusy(true); setError(null);
    try {
      await addProjectMember(project.project_id, { user_id: addUser, tier_id: addTier || null });
      setAddUser(''); setAddTier(''); setUserQuery(''); await fetchMembers();
    } catch (e) {
      setError(e.response?.status === 409 ? 'User already exists' : (e.response?.data?.detail || 'Failed to add member'));
    } finally { setBusy(false); }
  };

  const setMemberTier = async (userId, tierId) => {
    try { await updateProjectMember(project.project_id, userId, { tier_id: tierId || '' }); await fetchMembers(); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to update member'); }
  };

  const remove = async (userId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    try {
      await removeProjectMember(project.project_id, userId);
      // Removing the last row on a page beyond the first would otherwise land
      // on an empty page; step back one instead.
      if (members.length === 1 && rosterPage > 1) setRosterPage((p) => p - 1); else await fetchMembers();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to remove member'); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-xl font-semibold text-fg flex items-center gap-2"><UsersIcon className="w-5 h-5 text-orange" />Members — {project.name}</h2>
          <div className="flex items-center gap-2">
            <button onClick={resetTeamRL} title="Reset rate limits for the whole team"
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-field border border-field-border hover:border-orange text-fg-muted hover:text-fg rounded-lg text-xs">
              <Gauge className="w-4 h-4" />Reset team RLs
            </button>
            <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <p className="text-fg-muted text-sm mb-5">Members inherit the team tier <span className="text-fg">{project.tier_name || 'default'}</span>; set a per-member tier override below if needed.</p>

        {/* Add member */}
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <div className="relative flex-1">
            <Search className="w-3.5 h-3.5 text-fg-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={userQuery}
              onChange={(e) => { setUserQuery(e.target.value); setAddUser(''); setUserPickerOpen(true); }}
              onFocus={() => setUserPickerOpen(true)}
              onBlur={() => setTimeout(() => setUserPickerOpen(false), 150)}
              placeholder="Search users to add…"
              className="w-full pl-8 pr-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm"
            />
            {userPickerOpen && (
              <div className="absolute z-10 mt-1 w-full bg-panel border border-panel-border rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {userResults.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-fg-muted">{userSearching ? 'Searching…' : 'No matching users'}</div>
                ) : (
                  userResults.map((u) => {
                    const alreadyMember = memberIds.has(u.user_id);
                    return (
                      <button key={u.user_id} type="button" disabled={alreadyMember}
                        onMouseDown={(e) => e.preventDefault()} onClick={() => selectUser(u)}
                        className={`flex items-center justify-between gap-2 w-full text-left px-3 py-1.5 text-sm ${
                          alreadyMember ? 'text-fg-muted cursor-not-allowed' : `hover:bg-fg/5 ${addUser === u.user_id ? 'text-orange' : 'text-fg'}`}`}>
                        <span className="truncate">{u.user_email_id}</span>
                        {alreadyMember && <span className="shrink-0 text-[11px] text-fg-muted">Member</span>}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <select value={addTier} onChange={(e) => setAddTier(e.target.value)}
            className="px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm">
            <option value="">Team tier</option>
            {tiers.map((t) => <option key={t.tier_id} value={t.tier_id}>{t.name}</option>)}
          </select>
          <button onClick={add} disabled={busy} className="flex items-center gap-2 px-3 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm disabled:opacity-50">
            <UserPlus className="w-4 h-4" />Add
          </button>
        </div>

        {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
        {notice && <p className="text-xs text-green-400 mb-3">{notice}</p>}

        {/* Roster search + live count */}
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-3.5 h-3.5 text-fg-muted absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input type="text" value={rosterQueryInput} onChange={(e) => setRosterQueryInput(e.target.value)}
              placeholder="Search members…"
              className="w-full pl-8 pr-3 py-1.5 bg-field border border-field-border rounded-lg text-fg text-xs" />
          </div>
          <span className="text-xs text-fg-muted shrink-0">
            {memberTotal} member{memberTotal === 1 ? '' : 's'}{rosterQuery ? ' matching' : ''}
          </span>
        </div>

        {loading ? (
          <div className="text-center text-fg-muted py-10">Loading…</div>
        ) : members.length === 0 ? (
          <div className="text-center text-fg-muted py-10 bg-field/40 border border-field-border rounded-lg">
            {rosterQuery ? 'No members match that search.' : 'No members yet — add one above.'}
          </div>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between bg-field/40 border border-field-border rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="text-fg text-sm truncate">{m.email || m.user_id}</div>
                  {Object.keys(m.rl_overrides || {}).length > 0 && (
                    <div className="text-xs text-fg-muted">{Object.keys(m.rl_overrides).length} per-model RL override(s)</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select value={m.tier_id || ''} onChange={(e) => setMemberTier(m.user_id, e.target.value)}
                    className="px-2 py-1.5 bg-field border border-field-border rounded text-xs text-fg">
                    <option value="">Team tier</option>
                    {tiers.map((t) => <option key={t.tier_id} value={t.tier_id}>{t.name}</option>)}
                  </select>
                  <button onClick={() => setLimitsFor(m)} title="Per-model RL overrides" className="p-1.5 text-fg-muted hover:text-orange rounded"><SlidersHorizontal className="w-4 h-4" /></button>
                  <button onClick={() => resetMemberRL(m)} title="Reset this member's rate limits" className="p-1.5 text-fg-muted hover:text-orange rounded"><Gauge className="w-4 h-4" /></button>
                  <button onClick={() => remove(m.user_id)} title="Remove" className="p-1.5 text-fg-muted hover:text-red-400 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {memberTotal > ROSTER_PAGE_SIZE && (
          <div className="flex items-center justify-between pt-3 text-xs text-fg-muted">
            <span>
              Showing {(rosterPage - 1) * ROSTER_PAGE_SIZE + 1}–{Math.min(rosterPage * ROSTER_PAGE_SIZE, memberTotal)} of {memberTotal}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setRosterPage((p) => Math.max(1, p - 1))} disabled={rosterPage <= 1}
                className="p-1.5 rounded hover:bg-fg/5 disabled:opacity-30 disabled:hover:bg-transparent"><ChevronLeft className="w-4 h-4" /></button>
              <span>Page {rosterPage} of {Math.max(1, Math.ceil(memberTotal / ROSTER_PAGE_SIZE))}</span>
              <button onClick={() => setRosterPage((p) => (p * ROSTER_PAGE_SIZE < memberTotal ? p + 1 : p))}
                disabled={rosterPage * ROSTER_PAGE_SIZE >= memberTotal}
                className="p-1.5 rounded hover:bg-fg/5 disabled:opacity-30 disabled:hover:bg-transparent"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-5">
          <button onClick={onClose} className="px-6 py-2.5 bg-panel border border-panel-border text-fg rounded-lg hover:border-orange text-sm">Done</button>
        </div>

        {limitsFor && (
          <MemberLimitsModal project={project} member={limitsFor} tierModels={tierModelsFor(limitsFor)}
            onClose={() => setLimitsFor(null)}
            onSaved={() => { setLimitsFor(null); fetchMembers(); }} />
        )}
      </div>
    </div>
  );
}

// ── Limits & usage panel for a team (budget bars + RL + reset) ──
function ProjectLimitsModal({ project, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await getProjectUsage(project.project_id)).data); }
    catch { setNotice('Failed to load usage'); }
    finally { setLoading(false); }
  }, [project.project_id]);
  useEffect(() => { load(); }, [load]);

  const reset = async () => {
    if (!window.confirm(`Reset rate limits + budget for the whole team "${project.name}"? Clears every member's consumed quota.`)) return;
    setBusy(true);
    try { const r = await resetProjectRateLimits(project.project_id); setNotice(`Reset — ${r.data.cleared} counter(s) cleared.`); await load(); onChanged && onChanged(); }
    catch (e) { setNotice(e.response?.data?.detail || 'Reset failed'); }
    finally { setBusy(false); }
  };

  const RL = data?.rate_limits || {};
  const hasRL = ['per_minute', 'per_hour', 'per_6h', 'per_day'].some((k) => (RL[k] || 0) > 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h2 className="text-xl font-semibold text-fg flex items-center gap-2"><BarChart3 className="w-5 h-5 text-orange" />Limits &amp; usage — {project.name}</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-5 h-5" /></button>
        </div>

        {loading ? (
          <div className="text-center text-fg-muted py-10">Loading…</div>
        ) : (
          <>
            <p className="text-fg-muted text-sm mb-4">
              Tier <span className="text-fg">{data?.tier || 'default'}</span> · {data?.member_count} member{data?.member_count === 1 ? '' : 's'}.
              Budget below is the whole team (per-member cap × members).
            </p>

            <div className="text-xs font-semibold text-fg-muted mb-2">Budget (USD)</div>
            <div className="space-y-2.5 mb-5">
              {(data?.budget || []).map((w) => {
                const unlimited = !w.limit || w.limit <= 0;
                const pct = unlimited ? 0 : Math.min(100, Math.round((w.spent / w.limit) * 100));
                const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-orange';
                return (
                  <div key={w.window}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-fg-muted">{WIN_LABEL[w.window] || w.window}{w.per_member > 0 && <span className="text-fg-muted/60"> · ${w.per_member}/member</span>}</span>
                      <span className="text-fg-muted">${w.spent.toFixed(4)} / {unlimited ? '∞' : `$${w.limit.toFixed(2)}`}{!unlimited && ` (${pct}%)`}</span>
                    </div>
                    <div className="w-full h-2 bg-field rounded-full overflow-hidden">
                      <div className={`h-full ${unlimited ? 'bg-fg/10' : color} rounded-full transition-all`} style={{ width: unlimited ? '100%' : `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-xs font-semibold text-fg-muted mb-2">Requests (per model, whole team)</div>
            <div className="space-y-4 mb-5">
              {(data?.requests_per_model || []).map((entry, i) => (
                <div key={entry.model || `__all__${i}`}>
                  {entry.model && <p className="text-xs font-mono font-medium text-fg mb-2 truncate" title={entry.model}>{entry.model}</p>}
                  <div className="space-y-2.5">
                    {(entry.windows || []).map((w) => {
                      const unlimited = !w.limit || w.limit <= 0;
                      const pct = unlimited ? 0 : Math.min(100, Math.round((w.used / w.limit) * 100));
                      const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-yellow-500' : 'bg-orange';
                      return (
                        <div key={w.window}>
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-fg-muted">{WIN_LABEL[w.window] || w.window}{w.per_member > 0 && <span className="text-fg-muted/60"> · {w.per_member}/member</span>}</span>
                            <span className="text-fg-muted">{w.used} / {unlimited ? '∞' : w.limit}{!unlimited && ` (${pct}%)`}</span>
                          </div>
                          <div className="w-full h-2 bg-field rounded-full overflow-hidden">
                            <div className={`h-full ${unlimited ? 'bg-fg/10' : color} rounded-full transition-all`} style={{ width: unlimited ? '100%' : `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-xs font-semibold text-fg-muted mb-2">Rate limits (per member, requests/window)</div>
            <div className="flex flex-wrap gap-2 mb-5">
              {hasRL ? RL_FIELDS.map((r) => (
                <span key={r.key} className="px-2 py-0.5 bg-field border border-field-border rounded text-xs text-fg">
                  {r.label}: {RL[r.key] ? RL[r.key] : '∞'}
                </span>
              )) : <span className="text-fg-muted text-xs">No tier-wide rate limits (or set per-model in the tier).</span>}
            </div>

            {notice && <p className="text-xs text-green-400 mb-3">{notice}</p>}

            <div className="flex gap-3">
              <button onClick={reset} disabled={busy}
                className="flex items-center gap-2 px-4 py-2.5 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm disabled:opacity-50">
                <Gauge className="w-4 h-4" />{busy ? 'Resetting…' : 'Reset team limits'}
              </button>
              <button onClick={onClose} className="px-6 py-2.5 bg-panel border border-panel-border text-fg rounded-lg hover:border-orange text-sm">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);   // undefined=closed, null=new, obj=edit
  const [managing, setManaging] = useState(null);       // project whose members are open
  const [limitsFor, setLimitsFor] = useState(null);     // project whose limits panel is open
  const [menuFor, setMenuFor] = useState(null);         // project_id whose kebab menu is open
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([getProjects(), getTiers()]);
      setProjects(p.data.projects || []);
      setTiers(t.data.tiers || []);
    } catch { setMsg({ type: 'error', text: 'Failed to load teams' }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const flash = (text) => { setMsg({ type: 'success', text }); setTimeout(() => setMsg(null), 3500); };
  const remove = async (p) => {
    if (!window.confirm(`Delete team "${p.name}"? This removes all its memberships.`)) return;
    try { await deleteProject(p.project_id); flash('Team deleted'); load(); }
    catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed' }); }
  };
  const resetRL = async (p) => {
    if (!window.confirm(`Reset rate limits for the whole team "${p.name}"? Clears every member's consumed quota. Limits are unchanged.`)) return;
    try { const r = await resetProjectRateLimits(p.project_id); flash(`Team rate limits reset — ${r.data.cleared} counter(s) cleared.`); }
    catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed to reset rate limits' }); }
  };

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-fg">Teams</h1>
          <p className="text-fg-muted text-sm mt-1">A team bundles a tier (allowed models + rate limits) and its members. Members select the team in their client to use its models.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setEditing(null)} className="flex items-center gap-2 px-3 py-2 bg-orange hover:bg-orange-hover text-fg rounded-lg text-sm"><Plus className="w-4 h-4" />New team</button>
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-panel border border-panel-border text-fg-muted hover:text-fg rounded-lg text-sm"><RefreshCw className="w-4 h-4" />Refresh</button>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex justify-between ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{msg.text}</span><button onClick={() => setMsg(null)}>✕</button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-fg-muted py-16">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="text-center text-fg-muted py-16 bg-panel border border-panel-border rounded-xl">No teams yet — create one, attach a tier, then add members.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {projects.map((p) => (
            <div key={p.project_id} className="bg-panel border border-panel-border rounded-xl p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <FolderKanban className="w-4 h-4 text-orange" />
                    <span className="text-fg font-semibold truncate">{p.name}</span>
                  </div>
                  {p.description && <p className="text-fg-muted text-sm mt-1">{p.description}</p>}
                </div>
                <div className="relative shrink-0">
                  <button onClick={() => setMenuFor(menuFor === p.project_id ? null : p.project_id)} title="Options"
                    className="p-1.5 text-fg-muted hover:text-fg rounded"><MoreVertical className="w-4 h-4" /></button>
                  {menuFor === p.project_id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setMenuFor(null)} />
                      <div className="absolute right-0 top-8 z-20 w-48 bg-panel border border-panel-border rounded-lg shadow-xl py-1 text-sm">
                        <button onClick={() => { setMenuFor(null); setLimitsFor(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-fg hover:bg-fg/5"><BarChart3 className="w-4 h-4 text-fg-muted" />Limits &amp; usage</button>
                        <button onClick={() => { setMenuFor(null); setManaging(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-fg hover:bg-fg/5"><UsersIcon className="w-4 h-4 text-fg-muted" />Manage members</button>
                        <button onClick={() => { setMenuFor(null); setEditing(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-fg hover:bg-fg/5"><Pencil className="w-4 h-4 text-fg-muted" />Edit</button>
                        <button onClick={() => { setMenuFor(null); resetRL(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-fg hover:bg-fg/5"><Gauge className="w-4 h-4 text-fg-muted" />Reset rate limits</button>
                        <div className="my-1 border-t border-panel-border" />
                        <button onClick={() => { setMenuFor(null); remove(p); }} className="w-full flex items-center gap-2 px-3 py-2 text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" />Delete team</button>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="px-2 py-0.5 bg-orange/10 text-orange text-xs rounded">Tier: {p.tier_name || 'default'}</span>
                <span className="px-2 py-0.5 bg-field border border-field-border rounded text-xs text-fg flex items-center gap-1">
                  <UsersIcon className="w-3 h-3" />{p.member_count} member{p.member_count === 1 ? '' : 's'}
                </span>
              </div>

              <button onClick={() => setManaging(p)}
                className="mt-4 w-full py-2 bg-field border border-field-border hover:border-orange text-fg rounded-lg text-sm flex items-center justify-center gap-2">
                <UsersIcon className="w-4 h-4" />Manage members
              </button>
            </div>
          ))}
        </div>
      )}

      {editing !== undefined && (
        <ProjectModal project={editing} tiers={tiers}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); flash('Team saved'); load(); }} />
      )}

      {managing && (
        <MembersModal project={managing} tiers={tiers}
          onClose={() => { setManaging(null); load(); }} />
      )}

      {limitsFor && (
        <ProjectLimitsModal project={limitsFor}
          onClose={() => setLimitsFor(null)} onChanged={load} />
      )}
    </div>
  );
}
